import {
    AnimationBaseController,
    AnimationClipContainer,
    Collisions,
    GameRandom,
    IAnimationControllerConfiguration,
    Inject,
    RootHtml,
} from '@denishussar/html5-game-engine';
import { GameModel } from '../../Models/Game/GameModel';
import { GameLogic } from '../../Logic/GameLogic';
import { UnitController } from './Units/UnitController';
import { RoomController } from './Room/RoomController';
import { EnvironmentController } from './Environments/EnvironmentController';
import { MiniGameController } from './MiniGames/MiniGameController';
import { BonusController } from './Bonuses/BonusController';
import { BorderController } from './Environments/BorderController';
import { searchParam } from '../../Common/Utils';

export interface IGameControllerOptions {}

export class GameController extends AnimationBaseController {
    $model: GameModel;

    public onStartMatch: () => void;
    private _graphics: PIXI.Graphics;

    public constructor(
        @Inject private _options: IGameControllerOptions,
        @Inject private _rootHtml: RootHtml,
        @Inject private _random: GameRandom,
        @Inject private _gameLogic: GameLogic,
        @Inject private _collisions: Collisions
    ) {
        super('GameController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return {
            childControllers: {},
            onPointerMove: e => {
                let x =
                    (e.globalPosition.x -
                        this.display.transform.worldTransform.tx) /
                    this.display.transform.worldTransform.a;
                let y =
                    (e.globalPosition.y -
                        this.display.transform.worldTransform.ty) /
                    this.display.transform.worldTransform.d;

                this._gameLogic.moveCursor(x, y);
            },
            modelWatch: {
                pos: (pos: [number, number]) => {
                    this.x = pos[0] * this.$model.scale;
                    this.y = pos[1] * this.$model.scale; // + 200;
                },
                scale: (scale: number) => {
                    this.display.scale.set(this.$model.scale);
                },
            },
        };
    }

    protected async onInitialize() {
        this.display.scale.set(this.$model.scale);

        this._graphics = new PIXI.Graphics();
        this.display.addChild(this._graphics);

        this.getCurrentAnimationClipChildren(displayObject => {
            if (displayObject instanceof AnimationClipContainer) {
                const roomModel = this._gameLogic.addRoom(
                    [displayObject.x, displayObject.y],
                    displayObject.name
                );

                this.addChildController(
                    {
                        instanceName: roomModel.key,
                        view: RoomController,
                        model: roomModel,
                    },
                    roomModel.key
                );
            }
        });

        for (let i = 0; i < this.$model.unitList.count(); i++) {
            const unitModel = this.$model.unitList.elementAt(i);
            this.addChildController(
                {
                    view: UnitController,
                    model: unitModel,
                },
                'unit' + unitModel.index
            );
        }

        for (let i = 0; i < this.$model.roomList.count(); i++) {
            const room = this.$model.roomList.elementAt(i);

            for (let j = 0; j < room.environmentList.count(); j++) {
                const environment = room.environmentList.elementAt(j);

                this.addChildController(
                    {
                        view: EnvironmentController,
                        model: environment,
                    },
                    `room_${room.index}_environment_${j}`
                );
            }

            for (let j = 0; j < room.miniGameList.count(); j++) {
                const miniGame = room.miniGameList.elementAt(j);

                this.addChildController(
                    {
                        view: MiniGameController,
                        model: miniGame,
                    },
                    `room_${room.index}_miniGame_${miniGame.type}`
                );
            }

            for (let j = 0; j < room.bonusList.count(); j++) {
                const bonus = room.bonusList.elementAt(j);

                this.addChildController(
                    {
                        view: BonusController,
                        model: bonus,
                    },
                    `room_${room.index}_bonus_${bonus.type}`
                );
            }

            for (let j = 0; j < room.borderList.count(); j++) {
                const border = room.borderList.elementAt(j);

                this.addChildController(
                    {
                        view: BorderController,
                        model: border,
                        options: {
                            roomIndex: room.index,
                        },
                    },
                    `room_${room.index}_border`
                );
            }
        }

        const unit = this.$model.unitList.elementAt(0);

        this.x = unit.pos[0] * this.$model.scale;
        this.y = unit.pos[1] * this.$model.scale;
    }

    protected onLandscape(): void {}

    protected onPortrait(): void {}

    protected onUpdate(frameRate: number): void {
        const showDebugValue = searchParam('showDebug');
        const showDebug = showDebugValue === '1' || showDebugValue === 'true';

        if (showDebug) {
            this._graphics.clear();
            this._graphics.lineStyle(8, 0xff0000, 1);
            this._collisions.draw(this._graphics);
        }

        this.sortCurrentAnimationClipChildren((displayObject: any, view) => {
            if (view) {
                if (view.name == 'UnitController') {
                    displayObject.zOrder = view.$model.zOrder;
                    return true;
                }
                if (view.name == 'EnvironmentController') {
                    displayObject.zOrder = view.$model.zOrder;
                    return true;
                }
                if (view.name == 'BonusController') {
                    displayObject.zOrder = view.$model.zOrder;
                    return true;
                }
                if (view.name == 'MiniGameController') {
                    displayObject.zOrder = view.$model.zOrder;
                    return true;
                }
                if (view.name == 'BorderController') {
                    displayObject.zOrder = 10001;
                    return true;
                }
                if (view.name == 'OpponentCursorController') {
                    displayObject.zOrder = 10001;
                    return true;
                }
                if (view.name == 'CursorController') {
                    displayObject.zOrder = 10001;
                    return true;
                }
            }
        });
    }
}
